import { Link } from "@tanstack/react-router";
import { ShieldCheck, Wrench, Gauge, Sparkles, Stethoscope, ArrowRight } from "lucide-react";
import { getBusinessConfig } from "@/lib/site-config";
import { trackEvent } from "@/lib/tracking";
import { AnimatedSection } from "@/components/AnimatedSection";

const CARDS = [
  {
    icon: ShieldCheck,
    title: "MOT test",
    from: "£30–£40",
    note: "Typical for most cars — call to confirm class",
    body: "DVSA-authorised station V106376. Most tests done within the hour.",
    cta: "Book MOT",
    to: "/book" as const,
  },
  {
    icon: Wrench,
    title: "Interim service",
    from: "From £89",
    note: "Indicative only — final price after vehicle check",
    body: "Oil, filter, brakes and a 30-point safety check. Good for high-mileage drivers.",
    cta: "Get a quote",
    to: "/servicing" as const,
  },
  {
    icon: Gauge,
    title: "Full service",
    from: "From £149",
    note: "Indicative only — varies by car and parts",
    body: "Annual service — most cars every 12 months or 12,000 miles. Service book stamped.",
    cta: "Get a quote",
    to: "/servicing" as const,
  },
  {
    icon: Stethoscope,
    title: "Diagnostics",
    from: "Competitive",
    note: "Clear quote before any work — fee refunded against repair",
    body: "Proper investigation, not just a code reader. We find the actual cause.",
    cta: "Talk to us",
    to: "/diagnostics" as const,
  },
  {
    icon: Sparkles,
    title: "Bodywork",
    from: "Free estimate",
    note: "Send photos via WhatsApp",
    body: "Estimates given before any work. No surprise bills.",
    cta: "WhatsApp photos",
    to: "/bodywork" as const,
  },
] as const;

/**
 * "Transparent pricing — no surprises" 5-card guide row. Indicative
 * starting prices to help drivers plan before they call.
 */
export function PricingTransparency() {
  const biz = getBusinessConfig();
  return (
    <AnimatedSection>
      <section aria-labelledby="pricing-heading" className="section-y bg-secondary/40">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="max-w-2xl">
              <p className="eyebrow">Transparent pricing</p>
              <h2 id="pricing-heading" className="mt-3 display-lg text-foreground">
                Honest starting prices — no surprises.
              </h2>
              <p className="mt-4 text-muted-foreground leading-relaxed max-w-xl">
                We'll always confirm a quote before we start any work. Here are typical
                starting prices to help you plan — every car is different, so call us with your
                reg for an exact figure.
              </p>
            </div>
            <a
              href={biz.phoneHref}
              onClick={() => trackEvent("call_click", { location: "pricing_strip_header" })}
              className="inline-flex items-center gap-2 self-start rounded-lg border border-border bg-background px-4 py-2.5 text-sm font-bold text-foreground hover:border-primary/40 transition-colors"
            >
              Get an exact quote · {biz.phone}
            </a>
          </div>

          <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {CARDS.map(({ icon: Icon, title, from, note, body, cta, to }) => (
              <li
                key={title}
                className="relative flex flex-col rounded-2xl border border-border bg-card p-5 md:p-6 border-t-[3px] border-t-primary hover:border-primary/50 transition-colors"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-base font-bold text-foreground tracking-tight">{title}</h3>
                <p className="mt-3 text-2xl font-extrabold text-foreground tracking-tight">{from}</p>
                <p className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {note}
                </p>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed flex-1">{body}</p>
                <Link
                  to={to}
                  onClick={() => trackEvent("service_cta_click", { location: "pricing_strip", target: to })}
                  className="mt-5 inline-flex items-center gap-1 text-sm font-bold text-primary hover:text-primary/80"
                >
                  {cta} <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </li>
            ))}
          </ul>

          <p className="mt-6 text-xs text-muted-foreground max-w-2xl">
            Prices shown are indicative starting points for guidance only — your written quote always comes first and no work is carried out without your approval. Call with your reg for an accurate figure. Most enquiries are replied to during workshop hours; call is fastest.
          </p>
        </div>
      </section>
    </AnimatedSection>
  );
}
